import { useState, useEffect, useCallback } from 'preact/hooks'
import { salesforceApi } from '@/api/salesforce'
import { apiClient } from '@/api/client'
import type { SalesforceConnectionStatus } from '@/types/salesforce'
import { httpsUrlOrThrow } from '@/utils/externalUrl'

export function useSalesforceAuth(clientCompanyId: string, enabled = true) {
    const [status, setStatus] = useState<SalesforceConnectionStatus | null>(null)
    const [isLoading, setIsLoading] = useState(enabled && !!clientCompanyId)
    const [isConnecting, setIsConnecting] = useState(false)

    const checkStatus = useCallback(async () => {
        if (!clientCompanyId) {
            setIsLoading(false)
            return
        }
        setIsLoading(true)
        try {
            const response = await salesforceApi.getStatus(clientCompanyId)
            setStatus(response.data ?? { isConnected: false })
        } catch {
            setStatus({ isConnected: false })
        } finally {
            setIsLoading(false)
        }
    }, [clientCompanyId])

    useEffect(() => {
        if (enabled) {
            checkStatus()
        } else {
            // Panel closed: drop stale status and arm loading so the next open paints the
            // spinner immediately instead of flashing last session's status.
            setStatus(null)
            setIsLoading(true)
        }
    }, [enabled, checkStatus])

    // Initiate OAuth flow in a popup
    const connect = useCallback(async () => {
        setIsConnecting(true)
        try {
            const response = await salesforceApi.initiate(clientCompanyId)
            if (!response.data?.authorizationUrl) {
                throw new Error('Failed to get authorization URL')
            }

            const width = 600
            const height = 800
            const left = window.screenX + (window.outerWidth - width) / 2
            const top = window.screenY + (window.outerHeight - height) / 2
            const popup = window.open(
                httpsUrlOrThrow(response.data.authorizationUrl),
                'salesforce-oauth',
                `width=${width},height=${height},left=${left},top=${top},popup=yes`
            )

            if (!popup) {
                setIsConnecting(false)
                return
            }

            // Poll for popup close
            const pollTimer = setInterval(() => {
                if (popup.closed) {
                    clearInterval(pollTimer)
                    window.removeEventListener('message', handleMessage)
                    setIsConnecting(false)
                    checkStatus()
                }
            }, 500)

            // Listen for postMessage from the callback page
            const apiOrigin = new URL(apiClient.getBaseUrl()).origin
            const handleMessage = (event: MessageEvent) => {
                if (event.origin !== window.location.origin && event.origin !== apiOrigin) return
                if (event.source !== popup) return
                if (event.data?.type === 'salesforce-oauth-success') {
                    clearInterval(pollTimer)
                    window.removeEventListener('message', handleMessage)
                    popup.close()
                    setIsConnecting(false)
                    checkStatus()
                }
                if (event.data?.type === 'salesforce-oauth-error') {
                    clearInterval(pollTimer)
                    window.removeEventListener('message', handleMessage)
                    popup.close()
                    setIsConnecting(false)
                }
            }
            window.addEventListener('message', handleMessage)
        } catch {
            setIsConnecting(false)
        }
    }, [clientCompanyId, checkStatus])

    const disconnect = useCallback(async () => {
        try {
            await salesforceApi.disconnect(clientCompanyId)
            setStatus({ isConnected: false })
        } catch {
            // Disconnect failed — status unchanged
        }
    }, [clientCompanyId])

    return {
        isConnected: status?.isConnected ?? false,
        connectionId: status?.id,
        status: status?.status ?? '',
        scopes: status?.scopes ?? '',
        instanceUrl: status?.instanceUrl ?? null,
        // Health parity fields (surfaced for the summary's status badge + error detail).
        isSoftFailure: status?.isSoftFailure ?? false,
        consecutiveFailureCount: status?.consecutiveFailureCount ?? 0,
        recentErrors: status?.recentErrors ?? [],
        aiSummary: status?.aiSummary ?? null,
        // Summary readout fields.
        connectedAtUtc: status?.connectedAtUtc ?? null,
        lastOutboundSyncUtc: status?.lastOutboundSyncUtc ?? null,
        lastInboundSyncUtc: status?.lastInboundSyncUtc ?? null,
        isLoading,
        isConnecting,
        connect,
        disconnect,
        refreshStatus: checkStatus,
    }
}
